import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/utils'
import type { Ingredient, PantryState } from './types'

const STATE_STYLES: Record<PantryState, { container: string; label: string }> = {
  own: {
    container: 'bg-green-100 text-green-800 border-green-300',
    label: 'state.own',
  },
  need: {
    container: 'bg-red-100 text-red-800 border-red-300',
    label: 'state.need',
  },
  running_low: {
    container: 'bg-amber-100 text-amber-800 border-amber-300',
    label: 'state.runningLow',
  },
}

const STATES: PantryState[] = ['own', 'need', 'running_low']

interface PantryItemRowProps {
  ingredient: Ingredient
  onStateChange: (id: string, state: PantryState) => void
  isPending: boolean
}

export function PantryItemRow({ ingredient, onStateChange, isPending }: PantryItemRowProps) {
  const { t } = useTranslation('pantry')

  return (
    <div className="flex items-center justify-between gap-3 py-2">
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-ink truncate">{ingredient.name}</p>
        <p className="text-xs text-muted">{ingredient.canonicalUnit}</p>
      </div>
      <div
        role="group"
        aria-label={t('stateToggle.groupAriaLabel', { name: ingredient.name })}
        className="flex gap-1"
      >
        {STATES.map((state) => {
          const active = ingredient.pantryState === state
          const styles = STATE_STYLES[state]
          return (
            <button
              key={state}
              type="button"
              onClick={() => onStateChange(ingredient.id, state)}
              disabled={isPending}
              aria-label={t('stateToggle.ariaLabel', { name: ingredient.name, state: t(styles.label) })}
              aria-pressed={active}
              className={cn(
                'min-h-[44px] min-w-[44px] px-2 text-xs font-medium rounded-lg border',
                'motion-safe:transition-colors disabled:opacity-50',
                active
                  ? styles.container
                  : 'bg-transparent text-muted border-line hover:border-current',
              )}
            >
              {t(styles.label)}
            </button>
          )
        })}
      </div>
    </div>
  )
}
